import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { logAdConversion } from "@/lib/marketingAttribution";
import { Copy, Check, ExternalLink, Bookmark, BookmarkCheck, Zap, ArrowLeft, ShieldCheck, Calendar, BadgeCheck } from "lucide-react";
import PageBackground from "@/components/PageBackground";
import CouponCard from "@/components/CouponCard";
import Seo from "@/components/Seo";
import TrustBadge from "@/components/TrustBadge";
import SignInPromptModal from "@/components/SignInPromptModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

function discountLabel(c) {
  if (!c) return "";
  if (c.discount_type === "percent") return `${c.discount_value}% OFF`;
  if (c.discount_type === "fixed") return `$${c.discount_value} OFF`;
  if (c.discount_type === "shipping") return `FREE SHIP`;
  if (c.discount_type === "bogo") return `BOGO`;
  return "DEAL";
}

export default function CouponDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [coupon, setCoupon] = useState(null);
  const [related, setRelated] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [claimedCode, setClaimedCode] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [pendingClaim, setPendingClaim] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    api.get(`/coupons/${id}`).then((r) => {
      setCoupon(r.data);
      if (r.data?.category?.slug) {
        api.get(`/coupons?category=${r.data.category.slug}&limit=8`).then((rr) => {
          setRelated(rr.data.filter((c) => c.id !== r.data.id).slice(0, 3));
        });
      }
    });
    api.get(`/ai/similar/${id}`).then((r) => setSimilar(r.data || [])).catch(() => setSimilar([]));
  }, [id]);

  useEffect(() => {
    if (user && coupon) setSaved((user.saved_coupons || []).includes(coupon.id));
  }, [user, coupon]);

  if (!coupon) {
    return (
      <div className="relative">
        <PageBackground variant="cyan-lav" />
        <div className="max-w-7xl mx-auto px-6 py-24 relative z-10 text-slate-500">Loading…</div>
      </div>
    );
  }

  const reveal = async () => {
    try { await api.post(`/coupons/${coupon.id}/click`); } catch (_e) {}
    setRevealed(true);
    logAdConversion("first_offer_activation", { coupon_id: coupon.id, campaign_id: coupon.campaign_id });
  };
  const claimDynamic = async () => {
    if (!user) { setShowSignIn(true); return; }
    setClaiming(true);
    try {
      const affParam = new URLSearchParams(window.location.search).get("aff");
      const url = `/campaigns/${coupon.campaign_id}/claim${affParam ? `?aff=${encodeURIComponent(affParam)}` : ""}`;
      const { data } = await api.post(url);
      if (data.kind === "online_redirect") {
        toast.success("Redirecting you to the merchant…");
        const target = data.redirect_url || coupon.url;
        if (target) window.open(target, "_blank", "noopener,noreferrer");
        else toast.error("Merchant hasn't set a redirect URL yet.");
        logAdConversion("first_offer_activation", { coupon_id: coupon.id, campaign_id: coupon.campaign_id, kind: "online_redirect" });
        return;
      }
      if (data.code_ready === false) {
        setPendingClaim(data);
        toast.info(data.shopify_sync_status === "SHOPIFY_SYNC_FAILED"
          ? "Your claim is saved, but the store couldn't activate the code yet. Tap “Check again”."
          : "Your code is being activated on the store. We'll show it as soon as it's ready.");
        return;
      }
      setPendingClaim(null);
      setClaimedCode(data.code);
      setRevealed(true);
      if (data.reused) toast.info("You already claimed this — here's your code.");
      else toast.success("Your unique code is ready!");
      logAdConversion("first_offer_activation", { coupon_id: coupon.id, campaign_id: coupon.campaign_id, kind: "dynamic_code" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim code");
    } finally { setClaiming(false); }
  };
  const retrySync = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post(`/commerce/shopify/claims/${pendingClaim.id}/sync`);
      if (data.code_ready) { setPendingClaim(null); setClaimedCode(data.code); setRevealed(true); toast.success("Your unique code is ready!"); }
      else { setPendingClaim(data); toast.info(data.shopify_sync_status === "SHOPIFY_SYNC_FAILED" ? "The store still couldn't activate the code — please try again later." : "Still activating on the store — please try again shortly."); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not check the code yet");
    } finally { setClaiming(false); }
  };
  const copy = () => {
    const val = claimedCode || coupon.code;
    if (!val) return;
    navigator.clipboard.writeText(val);
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 1500);
  };
  const toggleSave = async () => {
    if (!user) return toast("Sign in to save coupons");
    try {
      if (saved) { await api.delete(`/user/save-coupon/${coupon.id}`); setSaved(false); }
      else { await api.post(`/user/save-coupon/${coupon.id}`); setSaved(true); toast.success("Saved"); }
    } catch { toast.error("Could not update saved coupons"); }
  };

  const expires = coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <div className="relative">
      <Seo
        title={`${coupon.title} — ${coupon.store?.name || "Cyannect"}`}
        description={coupon.description || `${discountLabel(coupon)} at ${coupon.store?.name}. Verified deal on Cyannect.`}
        image={coupon.store?.logo_url}
        path={`/coupon/${coupon.id}`}
        type="article"
        keywords={`${coupon.store?.name}, ${coupon.category?.name}, promo code, coupon, ${discountLabel(coupon)}`}
      />
      <PageBackground variant="cyan-lav" />

      <div className="max-w-6xl mx-auto px-6 py-10 relative z-10">
        <Link to="/coupons" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> All coupons
        </Link>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Left: main coupon panel */}
          <div className="md:col-span-2 glass-strong rounded-3xl p-8 relative overflow-hidden shimmer fade-up" data-testid="coupon-detail">
            {coupon.flash_sale && (
              <div className="absolute top-6 right-6 pf-chip pf-chip-lav">
                <Zap className="w-3 h-3" /> Flash sale
              </div>
            )}

            <div className="flex items-center gap-4">
              {coupon.store?.logo_url && (
                <img src={coupon.store.logo_url} alt={coupon.store.name} className="w-16 h-16 rounded-2xl border border-white/80 bg-white object-contain shadow" data-logo="1" />
              )}
              <div>
                <Link to={`/store/${coupon.store?.slug}`} className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 hover:text-cyan-700 flex items-center gap-1.5">
                  {coupon.store?.name}
                  {coupon.store_verified && (
                    <span className="inline-flex items-center gap-1 text-cyan-700 normal-case tracking-normal text-xs font-medium" title="Verified brand" data-testid="detail-verified-badge">
                      <BadgeCheck className="w-4 h-4" /> Verified brand
                    </span>
                  )}
                </Link>
                {coupon.category?.name && (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="pf-chip pf-chip-cyan">{coupon.category.name}</span>
                    <TrustBadge trust={coupon.trust} size="lg" testid="detail-trust-badge" />
                  </div>
                )}
              </div>
            </div>

            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-[-0.02em] mt-6 text-slate-900 leading-tight">
              {coupon.title}
            </h1>

            <div className="font-display text-6xl md:text-7xl font-medium tracking-tight mt-6 pf-gradient-text-2">
              {discountLabel(coupon)}
            </div>

            {coupon.description && (
              <p className="text-lg text-slate-600 mt-6 leading-relaxed max-w-2xl">{coupon.description}</p>
            )}

            <div className="mt-8">
              {pendingClaim ? (
                <div className="inline-flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50/80 px-6 py-4 max-w-md" data-testid="detail-code-pending">
                  <p className="text-sm text-amber-900" data-testid="detail-code-pending-message">
                    {pendingClaim.shopify_sync_status === "SHOPIFY_SYNC_FAILED"
                      ? "We couldn't activate your code on the store yet. Your claim is saved — tap below to try again."
                      : "Your claim is saved. We're activating your code on the store — it will appear here once it's ready to use."}
                  </p>
                  <button onClick={retrySync} disabled={claiming} className="pf-btn-primary px-6 py-2 rounded-full text-sm font-semibold self-start disabled:opacity-60" data-testid="detail-code-pending-retry">
                    {claiming ? "Checking…" : "Check again"}
                  </button>
                </div>
              ) : (coupon.code || claimedCode) ? (
                !revealed ? (
                  <button
                    onClick={reveal}
                    className="pf-btn-primary px-8 py-4 rounded-full font-semibold text-base inline-flex items-center gap-2"
                    data-testid="detail-reveal-code"
                  >
                    Reveal promo code →
                  </button>
                ) : (
                  <div className="inline-flex items-stretch gap-0 border border-white/80 rounded-full overflow-hidden bg-white/70 backdrop-blur w-full max-w-md">
                    <div className="flex-1 px-6 py-4 font-mono font-semibold text-lg text-slate-900 tracking-wider truncate" data-testid="detail-code-value">
                      {claimedCode || coupon.code}
                    </div>
                    <button onClick={copy} className="px-6 pf-btn-primary text-sm flex items-center gap-2 rounded-none" data-testid="detail-copy">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                )
              ) : coupon.campaign_id && coupon.campaign_type === "online" ? (
                (() => {
                  const base = `/go/campaign/${coupon.campaign_id}`;
                  const qs = new URLSearchParams(window.location.search);
                  const kept = new URLSearchParams();
                  for (const k of ["aff", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
                    if (qs.get(k)) kept.set(k, qs.get(k));
                  }
                  const href = kept.toString() ? `${base}?${kept}` : base;
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="pf-btn-primary px-8 py-4 rounded-full font-semibold text-base inline-flex items-center gap-2" data-testid="detail-tracker-link">
                      Get deal <ExternalLink className="w-4 h-4" />
                    </a>
                  );
                })()
              ) : coupon.campaign_id && coupon.campaign_mode === "dynamic" ? (
                <button
                  onClick={claimDynamic}
                  disabled={claiming}
                  className="pf-btn-primary px-8 py-4 rounded-full font-semibold text-base inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="detail-claim-campaign"
                >
                  <Sparkles className="w-4 h-4" />
                  {claiming ? "Generating your code…" : user ? "Get my unique code →" : "Sign in to claim →"}
                </button>
              ) : (
                <button onClick={reveal} className="pf-btn-primary px-8 py-4 rounded-full font-semibold text-base inline-flex items-center gap-2" data-testid="detail-get-deal">
                  Get deal <ExternalLink className="w-4 h-4" />
                </button>
              )}
              {coupon.url && revealed && (
                <a href={coupon.url} target="_blank" rel="noreferrer" className="ml-3 pf-btn-glass px-6 py-4 rounded-full font-semibold text-base inline-flex items-center gap-2" data-testid="detail-visit">
                  Visit {coupon.store?.name || "store"} <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-10">
              <div className="pf-card p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Views</div>
                <div className="font-display text-2xl mt-1 text-slate-900 tabular-nums">{coupon.views || 0}</div>
              </div>
              <div className="pf-card p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Redemptions</div>
                <div className="font-display text-2xl mt-1 text-slate-900 tabular-nums">{coupon.clicks || 0}</div>
              </div>
              {expires && (
                <div className="pf-card p-4">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Calendar className="w-3 h-3" /> Expires</div>
                  <div className="font-display text-2xl mt-1 text-slate-900">{expires}</div>
                </div>
              )}
            </div>
          </div>

          {/* Right: side panel */}
          <div className="space-y-4">
            <div className="pf-card p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Actions</div>
              <button onClick={toggleSave} className="mt-4 w-full pf-btn-glass py-3 rounded-full font-medium inline-flex items-center justify-center gap-2" data-testid="detail-save">
                {saved ? <BookmarkCheck className="w-4 h-4 text-cyan-600" /> : <Bookmark className="w-4 h-4" />}
                {saved ? "Saved" : "Save coupon"}
              </button>
              {coupon.store?.website && (
                <a href={coupon.store.website} target="_blank" rel="noreferrer" className="mt-3 w-full pf-btn-glass py-3 rounded-full font-medium inline-flex items-center justify-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Visit store
                </a>
              )}
              <Link to={`/store/${coupon.store?.slug}`} className="mt-3 w-full pf-btn-glass py-3 rounded-full font-medium inline-flex items-center justify-center gap-2">
                More from {coupon.store?.name}
              </Link>
            </div>

            <div className="pf-card p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Cyannect verified
              </div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                Every offer is reviewed by our team. If a code doesn't work, tap "Report" so we can update it in minutes.
              </p>
            </div>
          </div>
        </div>

        {similar.length > 0 && (
          <section className="mt-16" data-testid="similar-coupons">
            <div className="flex items-center gap-2 mb-2">
              <div className="pf-chip pf-chip-lav"><Sparkles className="w-3 h-3" /> AI similar</div>
            </div>
            <h2 className="font-display text-3xl font-medium tracking-tight text-slate-900 mb-6">
              You might also like
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {similar.map((c) => <CouponCard key={c.id} coupon={c} />)}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-3xl font-medium tracking-tight text-slate-900 mb-6">
              More from <span className="pf-gradient-text-2">{coupon.category?.name}</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((c) => <CouponCard key={c.id} coupon={c} />)}
            </div>
          </section>
        )}
      </div>

      <SignInPromptModal
        open={showSignIn}
        onClose={() => setShowSignIn(false)}
        storeName={coupon.store?.name}
      />
    </div>
  );
}
